import React, { useEffect, useRef, useState } from 'react';
import { theme } from '../theme';
import { useMaintenanceMode } from '../context/MaintenanceContext';
import { InfoTooltip } from './InfoTooltip';
import { ArchiveBoxIcon, CrossIcon, DownloadArrowIcon, LightningIcon, MassDownloadIcon, RestoreIcon } from './icons';
import { type AutoAction, type AutoIconKey, autoSentence, maintenanceAutoAction } from '../utils/autoAction';

// <purpose>THE Auto button with its announcement line (picked 2026-10-31, clickable mock v2): the
// line left of the levers says what one press will do („Archivieren: 15 Tickets älter als 1 Tag"),
// the levers sit between, the button stands a little apart at the right and wears its page's ONE
// icon. Replaces the icon-only button + the „N ausgewählt" chip (SelectionStrip, retired: the
// line's × clears the selection now). Registry: design/design-languages.html, entry `auto_button`.</purpose>
// <dependencies>utils/autoAction — the page's state table (what the press does); this file only
// draws it. useMaintenanceMode — maintenance turns every state into the quiet button.</dependencies>
// <constraints>
//   - The line's SLOT is always mounted at a fixed width, so the line arriving, changing or leaving
//     moves nothing in the title row (layout stability).
//   - Entrance: a fade with a 12 px drift from the button side, 400 ms; reduced motion = the fade
//     alone (index.css). The page's steady action (scope 'rule') plays it ONCE per page per session
//     (sessionStorage); a switch of verb or scope always plays it; a count changing inside one
//     scope only updates the words.
//   - The QUIET button (nothing to do): grey outline, grey page icon, aria-disabled (it stays in the
//     tab order), a press does nothing. Only the quiet button hovers, and only with a hint.
//   - The line announces politely (aria-live); the button's name is the same sentence.
// </constraints>

const ICONS: Record<AutoIconKey, React.FC<{ size?: number; color?: string }>> = {
  lightning: LightningIcon,
  archive: ArchiveBoxIcon,
  restore: RestoreIcon,
  download: DownloadArrowIcon,
  'mass-download': MassDownloadIcon,
};

/** The line's reserved width — the longest fixed sentence plus its ×, at 13 px. */
export const AUTO_LINE_WIDTH = 400;
export const AUTO_ENTRANCE_MS = 400;
const AUTO_LEAVE_MS = 160;
const SESSION_PREFIX = 'stx-auto-entrance:';

const sessionHas = (k: string): boolean => { try { return sessionStorage.getItem(SESSION_PREFIX + k) === '1'; } catch { return false; } };
const sessionMark = (k: string): void => { try { sessionStorage.setItem(SESSION_PREFIX + k, '1'); } catch { /* private mode: plays every visit */ } };

type Phase = 'in' | 'shown' | 'out';

/** The announcement line. `page` names the page for the once-per-session memory. */
export const AutoLine: React.FC<{ page: string; action: AutoAction; onClear: () => void }> = ({ page, action, onClear }) => {
  const key = action.verb ? `${action.verb}|${action.scope}` : 'idle';
  const [shown, setShown] = useState<AutoAction | null>(null);
  const [phase, setPhase] = useState<Phase>('shown');
  const [play, setPlay] = useState(0);
  const shownKey = useRef<string | null>(null);
  const everShown = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useEffect(() => {
    if (!action.verb) {
      if (timer.current) clearTimeout(timer.current);
      shownKey.current = 'idle';
      setShown(null);
      return;
    }
    if (key === shownKey.current) { setShown(action); return; }
    // The first line this page shows (data loading counts as not yet shown): the steady action plays
    // once per session. Anything after that is a switch, and a switch always plays.
    const entering = !everShown.current;
    const memo = `${page}|${key}`;
    const plays = !entering || action.scope !== 'rule' || !sessionHas(memo);
    if (entering && action.scope === 'rule') sessionMark(memo);
    const leaving = shownKey.current !== null && shownKey.current !== 'idle';
    shownKey.current = key;
    everShown.current = true;
    if (timer.current) clearTimeout(timer.current);
    const show = () => { setShown(action); setPhase(plays ? 'in' : 'shown'); setPlay(n => n + 1); };
    if (leaving && plays) { setPhase('out'); timer.current = setTimeout(show, AUTO_LEAVE_MS); } else show();
    // The action object is rebuilt on every page render; what matters is its words.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, action.what, action.clearable, page]);

  return (
    <div
      data-testid="auto-line"
      aria-live="polite"
      style={{
        width: AUTO_LINE_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        fontSize: 13, color: theme.colors.textSecondary, whiteSpace: 'nowrap', overflow: 'visible',
      }}
    >
      {shown?.verb && (
        <span
          key={play}
          data-testid="auto-line-text"
          data-phase={phase}
          className={phase === 'in' ? 'stx-say-in' : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            animation: phase === 'in' ? `stx-sayIn ${AUTO_ENTRANCE_MS}ms ease-out both`
              : phase === 'out' ? `stx-fadeOut ${AUTO_LEAVE_MS}ms ease-in both` : undefined,
          }}
        >
          <span>{shown.verb}: <b style={{ color: theme.colors.navy, fontWeight: 600 }}>{shown.what}</b></span>
          {shown.clearable && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Auswahl aufheben"
              title="Auswahl aufheben"
              style={{
                border: 'none', background: 'none', cursor: 'pointer', padding: 2, borderRadius: 4,
                display: 'inline-flex', alignItems: 'center', color: theme.colors.navyMuted,
              }}
            >
              <CrossIcon size={12} />
            </button>
          )}
        </span>
      )}
    </div>
  );
};

/** A lever beside the Auto button (selection mode, the Excel lever). On = pressed: pale fill, navy
 *  1.5 px frame, its solid icon — never filled navy, which is the Auto button's alone. */
export const Lever: React.FC<{ on: boolean; label: string; onClick: () => void; children: React.ReactNode }> = ({ on, label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    aria-label={label}
    title={label}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
      width: 40, height: 40, padding: 0, flexShrink: 0,
      background: on ? theme.colors.navyTint : 'transparent',
      border: on ? `1.5px solid ${theme.colors.navy}` : `1px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
      color: on ? theme.colors.navy : theme.colors.navyMid,
      transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    }}
  >
    {children}
  </button>
);

/** The button alone. Prefer AutoBar, which draws the line with it. */
export const AutoButton: React.FC<{ action: AutoAction; onPress: () => void; busy?: boolean }> = ({ action, onPress, busy }) => {
  const maintenance = useMaintenanceMode();
  const a = maintenance ? maintenanceAutoAction(action) : action;
  const quiet = !a.canAct;
  const Icon = ICONS[a.icon];
  const button = (
    <button
      type="button"
      data-testid="auto-button"
      onClick={() => { if (!quiet && !busy) onPress(); }}
      aria-disabled={quiet || undefined}
      aria-busy={busy || undefined}
      aria-label={autoSentence(a)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
        width: 40, height: 40, padding: 0, marginLeft: 10, flexShrink: 0,
        fontFamily: theme.font.family,
        backgroundColor: quiet ? 'transparent' : theme.colors.navy,
        border: `1.5px solid ${quiet ? theme.colors.borderLight : theme.colors.navy}`,
        color: quiet ? theme.colors.textOnDark : theme.colors.white,
        borderRadius: theme.borderRadius.sm,
        cursor: quiet ? 'default' : busy ? 'progress' : 'pointer',
        transition: 'background 0.25s ease, color 0.25s ease, border-color 0.25s ease',
      }}
    >
      <Icon size={16} />
    </button>
  );
  return quiet && a.hint ? <InfoTooltip text={a.hint} placement="below">{button}</InfoTooltip> : button;
};

/** The whole toolbar tail: line · levers · Auto button. */
export const AutoBar: React.FC<{
  page: string;
  action: AutoAction;
  onPress: () => void;
  onClear: () => void;
  busy?: boolean;
  /** The levers (selection mode, Excel), in order. */
  children?: React.ReactNode;
}> = ({ page, action, onPress, onClear, busy, children }) => {
  const maintenance = useMaintenanceMode();
  const lineAction = maintenance ? maintenanceAutoAction(action) : action;
  const button = <AutoButton action={action} onPress={onPress} busy={busy} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
      <AutoLine page={page} action={lineAction} onClear={onClear} />
      {children}
      {button}
    </div>
  );
};
